import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';

export type AISafetyRun = Record<string, unknown>;
export type AISafetyInputType = 'LLM_APP' | 'ML_APP' | 'BOTH' | 'LEGACY' | 'UNKNOWN';

function getArray(value: unknown): AISafetyRun[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is AISafetyRun => Boolean(item) && typeof item === 'object');
  }
  if (value && typeof value === 'object') {
    return [value as AISafetyRun];
  }
  return [];
}

/** Prefer `ai_safety`; fall back to legacy `abuse_ai` when absent. */
export function resolveSafetyArrayFromBucket(bucket: Record<string, unknown> | null | undefined): AISafetyRun[] {
  if (!bucket) return [];
  const primary = bucket.ai_safety;
  if (primary != null) return getArray(primary);
  return getArray(bucket.abuse_ai);
}

/** Resolve safety runs from project results or 360 custom payload. */
export function collectSafetyRuns(projectResults: unknown, customData?: unknown): AISafetyRun[] {
  if (customData != null) {
    if (Array.isArray(customData)) {
      return getArray(customData).filter(isWorkbenchNonRerunRun);
    }
    if (typeof customData === 'object') {
      return [customData as AISafetyRun];
    }
  }

  if (!projectResults || typeof projectResults !== 'object') return [];
  const pr = projectResults as Record<string, unknown>;
  const sections: AISafetyRun[] = [];

  for (const rootKey of ['results', 'collection_results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    sections.push(...resolveSafetyArrayFromBucket(root as Record<string, unknown>).filter(isWorkbenchNonRerunRun));
  }

  if (pr.ai_safety != null) {
    sections.push(...getArray(pr.ai_safety).filter(isWorkbenchNonRerunRun));
  } else if (pr.abuse_ai != null) {
    sections.push(...getArray(pr.abuse_ai).filter(isWorkbenchNonRerunRun));
  }

  return dedupeRuns(sections);
}

function dedupeRuns(items: AISafetyRun[]): AISafetyRun[] {
  const seen = new Set<string>();
  const out: AISafetyRun[] = [];
  items.forEach((item, index) => {
    const id = String(item.result_id ?? '') || `${String(item.title ?? 'safety')}-${String(item.timestamp ?? index)}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ ...item, result_id: item.result_id ?? id });
  });
  return sortRunsNewestFirst(out);
}

function normalizeTimestamp(ts: unknown): number {
  if (ts == null) return 0;
  if (typeof ts === 'number' && !Number.isNaN(ts)) return ts;
  if (typeof ts === 'string') {
    const n = Date.parse(ts);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return d;
    if (typeof d === 'string') {
      const n = Date.parse(d);
      return Number.isNaN(n) ? 0 : n;
    }
  }
  return 0;
}

export function sortRunsNewestFirst(runs: AISafetyRun[]): AISafetyRun[] {
  return [...runs].sort((a, b) => normalizeTimestamp(b.timestamp) - normalizeTimestamp(a.timestamp));
}

/** New AI Safety schema exposes risk_score / score_breakdown. */
export function isNewSafetySchema(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  return run.risk_score != null || run.score_breakdown != null || run.evidence != null;
}

export const LLM_CONTROL_KEYS = ['prompt_abuse', 'harm_detection', 'content_safety', 'leakage'] as const;
export const ML_CONTROL_KEYS = [
  'unsafe_decision_output',
  'protected_class_harm',
  'regulated_decisioning',
  'model_misuse',
  'ml_safety_drift'
] as const;

export const LLM_CONTROL_LABELS: Record<(typeof LLM_CONTROL_KEYS)[number], string> = {
  prompt_abuse: 'Prompt Abuse Risk',
  harm_detection: 'Harm Detection Risk',
  content_safety: 'Content Safety Risk',
  leakage: 'Data Leakage Risk'
};

export const ML_CONTROL_LABELS: Record<(typeof ML_CONTROL_KEYS)[number], string> = {
  unsafe_decision_output: 'Unsafe Decision Output',
  protected_class_harm: 'Protected Class Harm',
  regulated_decisioning: 'Regulated Decisioning',
  model_misuse: 'Model Misuse Risk',
  ml_safety_drift: 'ML Safety Drift'
};

export function readControls(run: AISafetyRun | null | undefined): Record<string, Record<string, unknown>> {
  if (!run) return {};
  const breakdown = run.score_breakdown;
  if (breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)) {
    const controls = (breakdown as Record<string, unknown>).controls;
    if (controls && typeof controls === 'object' && !Array.isArray(controls)) {
      return controls as Record<string, Record<string, unknown>>;
    }
  }
  return {};
}

export function resolveInputType(run: AISafetyRun | null | undefined): AISafetyInputType {
  if (!run) return 'UNKNOWN';
  const raw = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'LLM_APP' || raw === 'LLM') return 'LLM_APP';
  if (raw === 'ML_APP' || raw === 'ML') return 'ML_APP';
  if (raw === 'BOTH' || raw === 'HYBRID') return 'BOTH';

  if (isNewSafetySchema(run)) {
    const controls = readControls(run);
    const keys = Object.keys(controls);
    const hasLlm = keys.some(k => k.startsWith('llm_') || (LLM_CONTROL_KEYS as readonly string[]).includes(k));
    const hasMl = keys.some(k => k.startsWith('ml_') || (ML_CONTROL_KEYS as readonly string[]).includes(k));
    if (hasLlm && hasMl) return 'BOTH';
    if (hasMl) return 'ML_APP';
    if (hasLlm) return 'LLM_APP';
  }

  if (run.LLM_ABUSE_ANALYSIS || run.llm_abuse_analysis) {
    if (run.ML_ABUSE_DETECTION || run.ML_Abuse_Detection) return 'BOTH';
    return 'LLM_APP';
  }
  if (run.ML_ABUSE_DETECTION || run.ML_Abuse_Detection) return 'ML_APP';
  return 'LEGACY';
}

export function runHasMlSafetyData(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  const ml = run.ML_ABUSE_DETECTION ?? run.ML_Abuse_Detection;
  if (Array.isArray(ml) && ml.length > 0) return true;

  const inputType = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (inputType === 'ML_APP' || inputType === 'ML') return true;
  if (inputType === 'BOTH' || inputType === 'HYBRID') return true;

  if (isNewSafetySchema(run)) {
    return runHasMlPrefixedControls(run);
  }

  return false;
}

export function runHasLlmSafetyData(run: AISafetyRun | null | undefined): boolean {
  if (!run) return false;
  if (run.LLM_ABUSE_ANALYSIS || run.llm_abuse_analysis) return true;

  const inputType = String(run.input_type ?? run.inputType ?? '')
    .trim()
    .toUpperCase();
  if (inputType === 'LLM_APP' || inputType === 'LLM') return true;
  if (inputType === 'BOTH' || inputType === 'HYBRID') return true;

  if (isNewSafetySchema(run)) {
    const keys = Object.keys(readControls(run));
    return keys.some(k => k.startsWith('llm_') || (LLM_CONTROL_KEYS as readonly string[]).includes(k));
  }

  return false;
}

export function readDeclaredInputType(run: AISafetyRun | null | undefined): string {
  return String(run?.input_type ?? run?.inputType ?? '')
    .trim()
    .toUpperCase();
}

export function isStrictLlmAppRun(run: AISafetyRun | null | undefined): boolean {
  const raw = readDeclaredInputType(run);
  return raw === 'LLM_APP' || raw === 'LLM';
}

export function readMlPrefixedControlKeys(run: AISafetyRun | null | undefined): string[] {
  return Object.keys(readControls(run)).filter(key => key.startsWith('ml_'));
}

export function runHasMlPrefixedControls(run: AISafetyRun | null | undefined): boolean {
  return readMlPrefixedControlKeys(run).length > 0;
}

export function readMlPrefixedControls(run: AISafetyRun | null | undefined): Record<string, Record<string, unknown>> {
  const controls = readControls(run);
  return Object.fromEntries(Object.entries(controls).filter(([key]) => key.startsWith('ml_')));
}

export function resolveMlPrefixedControlEntry(
  controls: Record<string, Record<string, unknown>>,
  baseKey: (typeof ML_CONTROL_KEYS)[number]
): Record<string, unknown> | undefined {
  return controls[`ml_${baseKey}`];
}

export function runBelongsToSafetyLane(run: AISafetyRun | null | undefined, lane: 'ml' | 'llm'): boolean {
  if (!run) return false;
  return lane === 'llm' ? isStrictLlmAppRun(run) : runHasMlPrefixedControls(run);
}

export function filterRunsBySafetyLane(runs: AISafetyRun[], lane: 'ml' | 'llm'): AISafetyRun[] {
  return runs.filter(run => runBelongsToSafetyLane(run, lane));
}

export function matchesSafetyRunId(run: AISafetyRun, id: string): boolean {
  if (!id) return false;
  const rid = String(run.result_id ?? '');
  if (rid && rid === id) return true;
  return createVersionId(run, 0) === id;
}

function normalizeControlToken(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_');
}

export function isMlControlKey(key: string): boolean {
  const normalized = normalizeControlToken(key);
  if (normalized.startsWith('ml_')) return true;
  if (normalized.startsWith('llm_')) return false;
  const base = normalized.replace(/^ml_/, '');
  return (ML_CONTROL_KEYS as readonly string[]).includes(base);
}

export function isLlmControlKey(key: string): boolean {
  const normalized = normalizeControlToken(key);
  if (normalized.startsWith('llm_')) return true;
  if (normalized.startsWith('ml_')) return false;
  return (LLM_CONTROL_KEYS as readonly string[]).includes(normalized);
}

export function matchesSafetyControlLane(controlName: string, lane: 'ml' | 'llm'): boolean {
  const normalized = normalizeControlToken(controlName);
  if (lane === 'ml') {
    return normalized.startsWith('ml_') || isMlControlKey(normalized);
  }
  if (isLlmControlKey(normalized)) return true;
  return Object.values(LLM_CONTROL_LABELS).some(label => normalizeControlToken(label) === normalized);
}

export function filterControlEntriesByLane(
  controls: Record<string, Record<string, unknown>>,
  lane: 'ml' | 'llm'
): Record<string, Record<string, unknown>> {
  return Object.fromEntries(
    Object.entries(controls).filter(([key]) => (lane === 'ml' ? key.startsWith('ml_') : isLlmControlKey(key)))
  );
}

export function controlScore(entry: Record<string, unknown> | undefined): number | null {
  if (!entry) return null;
  const raw = entry.score ?? entry.weighted_sum ?? entry.Score ?? entry.Weighted_Sum;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function resolveControlEntry(
  controls: Record<string, Record<string, unknown>>,
  key: string,
  prefix?: 'llm' | 'ml'
): Record<string, unknown> | undefined {
  if (prefix === 'ml') {
    return controls[`ml_${key}`] ?? controls[key];
  }
  if (controls[key]) return controls[key];
  if (prefix) {
    const prefixed = `${prefix}_${key}`;
    if (controls[prefixed]) return controls[prefixed];
  }
  return undefined;
}

export function formatSafetyVersionTimestamp(ts: unknown): string {
  if (ts == null || ts === '') return '';
  if (typeof ts === 'number' && !Number.isNaN(ts)) {
    try {
      return new Date(ts).toISOString();
    } catch {
      return String(ts);
    }
  }
  if (typeof ts === 'string') return ts.trim();
  if (typeof ts === 'object' && ts !== null && '$date' in (ts as Record<string, unknown>)) {
    const d = (ts as { $date?: unknown }).$date;
    if (typeof d === 'number') return new Date(d).toISOString();
    if (typeof d === 'string') return d;
  }
  return '';
}

export function createVersionId(item: AISafetyRun, fallbackIndex: number): string {
  return String(item.result_id ?? '') || `${String(item.title ?? 'safety')}-${String(item.timestamp ?? fallbackIndex)}`;
}

export function pickLatestSafetyRun(projectResults: unknown): AISafetyRun | null {
  if (!projectResults || typeof projectResults !== 'object') return null;
  const pr = projectResults as Record<string, unknown>;
  for (const rootKey of ['collection_results', 'results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    const bucket = root as Record<string, unknown>;
    if (bucket.ai_safety != null) {
      return pickLatestCollectionRun(bucket.ai_safety as AISafetyRun[]) as AISafetyRun | null;
    }
  }
  for (const rootKey of ['collection_results', 'results', 'feature_results'] as const) {
    const root = pr[rootKey];
    if (!root || typeof root !== 'object') continue;
    const bucket = root as Record<string, unknown>;
    if (bucket.abuse_ai != null) {
      return pickLatestCollectionRun(bucket.abuse_ai as AISafetyRun[]) as AISafetyRun | null;
    }
  }
  return null;
}

export function resolveSafetyDataFromResults(results: Record<string, unknown> | null | undefined): unknown {
  if (!results) return undefined;
  return results.ai_safety ?? results.abuse_ai;
}
